import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Col, Row, Switch, Typography } from 'antd';
import { TbCircle, TbCloudLock, TbFingerprintScan, TbMailSpark, TbMinusVertical } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { workspaceService } from '@/services/database';
import { PlatformAlert } from '@/shared/components/alerts';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore, useWorkspaceStore } from '@/stores';
import { WorkspaceAuthorizedAuthenticationMethods } from '@/types';
import { SessionLoginMethod } from '@/types/database/generated/Session';

import AdvancedSettingsSectionHeader from '../../../components/AdvancedSettingsSectionHeader';

type ApiErrorLike = {
    response?: {
        data?: {
            error?: string;
        };
    };
};

const getApiErrorDescription = (error: unknown, fallback: string) =>
    (error as ApiErrorLike)?.response?.data?.error ?? fallback;

const GoogleSVG = () => (
    <svg width='16' height='16' viewBox='0 0 256 262' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='xMidYMid'>
        <path
            d='M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027'
            fill='#4285F4'
        />
        <path
            d='M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1'
            fill='#34A853'
        />
        <path
            d='M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782'
            fill='#FBBC05'
        />
        <path
            d='M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251'
            fill='#EB4335'
        />
    </svg>
);

const MicrosoftSVG = () => (
    <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 256 262' preserveAspectRatio='xMidYMid'>
        <path fill='#F1511B' d='M121.666 121.666H0V0h121.666z' />
        <path fill='#80CC28' d='M256 121.666H134.335V0H256z' />
        <path fill='#00ADEF' d='M121.663 256.002H0V134.336h121.663z' />
        <path fill='#FBBC09' d='M256 256.002H134.335V134.336H256z' />
    </svg>
);

const AuthorizedMethodsSection: FC = (): JSX.Element => {
    const { selectedUser: user } = useUserStore();
    const { selectedWorkspace: workspace } = useWorkspaceStore();
    const { token } = useAntDesignTheme();
    const { hasFeature } = useEntitlements();

    const [enabled, setEnabled] = useState(false);
    const [policy, setPolicy] = useState<WorkspaceAuthorizedAuthenticationMethods>();
    const [methods, setMethods] = useState<SessionLoginMethod[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const canManage = ['Owner', 'Admin'].includes(String(user?.activeMember?.permission));
    const canUseAuthorizedMethods = hasFeature('authorizedAuthenticationMethods');
    const workspaceId = workspace?.id;
    const disabled = !canUseAuthorizedMethods || !canManage || loading || saving;
    const shouldShowPanel = enabled || Boolean(policy?.enabled);
    const lastUpdatedLabel = policy?.updatedAt ? new Date(policy.updatedAt).toLocaleString() : undefined;

    const options = useMemo<
        Array<{
            description: string;
            icon: ReactNode;
            label: string;
            method: SessionLoginMethod;
        }>
    >(
        () => [
            {
                description: 'Allow members to sign in with Google OAuth and access this workspace.',
                icon: <GoogleSVG />,
                label: 'Google',
                method: SessionLoginMethod.GOOGLE,
            },
            {
                description: 'Allow members to sign in with Microsoft OAuth and access this workspace.',
                icon: <MicrosoftSVG />,
                label: 'Microsoft',
                method: SessionLoginMethod.MICROSOFT,
            },
            {
                description: 'Allow members to request secure email sign-in links.',
                icon: <TbMailSpark size='1rem' />,
                label: 'Magic Link',
                method: SessionLoginMethod.MAGIC,
            },
            {
                description: 'Allow members to authenticate with registered device passkeys.',
                icon: <TbFingerprintScan size='1rem' />,
                label: 'Passkey',
                method: SessionLoginMethod.PASSKEY,
            },
            {
                description: 'Allow members to authenticate through your WorkOS SAML SSO connection.',
                icon: <TbCloudLock size='1rem' />,
                label: 'SAML SSO',
                method: SessionLoginMethod.SSO,
            },
        ],
        [],
    );
    const allMethods = useMemo(() => options.map((option) => option.method), [options]);

    const handleGetAuthorizedMethods = useCallback(async () => {
        if (!workspaceId) {
            return;
        }

        try {
            setLoading(true);

            const response = await workspaceService.getAuthorizedAuthenticationMethods(workspaceId);
            const incomingMethods = response.methods?.length ? response.methods : allMethods;

            setPolicy(response);
            setEnabled(response.enabled);
            setMethods(incomingMethods);
        } catch (error) {
            captureException(error);
        } finally {
            setLoading(false);
        }
    }, [allMethods, workspaceId]);

    useEffect(() => {
        handleGetAuthorizedMethods();
    }, [handleGetAuthorizedMethods]);

    const persistMethods = async (
        nextEnabled: boolean,
        nextMethods: SessionLoginMethod[],
        rollbackEnabled: boolean,
        rollbackMethods: SessionLoginMethod[],
    ) => {
        if (!workspace?.id) {
            return;
        }

        if (nextEnabled && nextMethods.length === 0) {
            toast.error('Select at least one authorized authentication method before enabling enforcement.');

            return;
        }

        setEnabled(nextEnabled);
        setMethods(nextMethods);

        try {
            setSaving(true);

            const response = await workspaceService.updateAuthorizedAuthenticationMethods(workspace.id, {
                enabled: nextEnabled,
                methods: nextMethods,
            });

            setPolicy(response);
            setEnabled(response.enabled);
            setMethods(response.methods?.length ? response.methods : allMethods);
        } catch (error) {
            setEnabled(rollbackEnabled);
            setMethods(rollbackMethods);
            captureException(error);
            toast.error('Could not update authorized methods', {
                description: getApiErrorDescription(error, 'Review the selected methods and try again.'),
            });
        } finally {
            setSaving(false);
        }
    };

    const handleEnabledChange = (checked: boolean) => {
        const nextMethods = methods.length ? methods : allMethods;

        void persistMethods(checked, nextMethods, enabled, methods);
    };

    const toggleMethod = (method: SessionLoginMethod, checked: boolean) => {
        const nextMethods = checked
            ? methods.includes(method)
                ? methods
                : [...methods, method]
            : methods.filter((candidate) => candidate !== method);

        if (enabled && nextMethods.length === 0) {
            toast.error('At least one authentication method must remain enabled.');

            return;
        }

        void persistMethods(enabled, nextMethods, enabled, methods);
    };

    return (
        <>
            <AdvancedSettingsSectionHeader
                description='Only allow users to join this workspace with certain methods.'
                icon={<TbCloudLock size='1rem' />}
                plan='enterprise'
                title='Authorized Methods'
                extra={
                    <Switch
                        disabled={disabled}
                        loading={loading}
                        size='default'
                        style={{ verticalAlign: '-1rem' }}
                        checked={enabled}
                        checkedChildren={<TbMinusVertical style={{ verticalAlign: '-0.1rem' }} />}
                        unCheckedChildren={<TbCircle style={{ verticalAlign: '-0.1rem' }} />}
                        onChange={handleEnabledChange}
                    />
                }
            />
            {shouldShowPanel && (
                <div
                    style={{
                        background: token.colorBgContainer,
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                        marginBottom: '1.5rem',
                        padding: '1rem',
                    }}
                >
                    {enabled && (
                        <>
                            {lastUpdatedLabel && (
                                <small
                                    style={{
                                        color: token.colorTextTertiary,
                                        display: 'block',
                                        marginBottom: '0.75rem',
                                    }}
                                >
                                    Last updated {lastUpdatedLabel}
                                </small>
                            )}
                            {!canUseAuthorizedMethods && (
                                <PlatformAlert
                                    showIcon
                                    type='warning'
                                    message='Authorized methods require Enterprise'
                                    description='Upgrade the workspace plan before enforcing workspace authentication method controls.'
                                    style={{ marginBottom: '1rem' }}
                                />
                            )}
                            {!canManage && (
                                <PlatformAlert
                                    showIcon
                                    type='info'
                                    message='Owner or Admin permission required'
                                    description='Only workspace owners and admins can change authorized authentication methods.'
                                    style={{ marginBottom: '1rem' }}
                                />
                            )}
                            <div
                                style={{
                                    display: 'grid',
                                    columnGap: '2rem',
                                    rowGap: '1rem',
                                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                }}
                            >
                                {options.map((option) => {
                                    const checked = methods.includes(option.method);

                                    return (
                                        <Row
                                            key={option.method}
                                            align='top'
                                            gutter={[12, 12]}
                                            wrap={false}
                                            style={{
                                                background: token.colorBgContainer,
                                                border: `1px dashed ${token.colorBorder}`,
                                                borderRadius: token.borderRadius,
                                                minHeight: '5.25rem',
                                                padding: '0.75rem',
                                            }}
                                        >
                                            <Col flex='none'>
                                                <Avatar
                                                    shape='square'
                                                    size={24}
                                                    style={{
                                                        background: token.colorBgElevated,
                                                        border: `1px solid ${token.colorBorder}`,
                                                        color: token.colorTextTertiary,
                                                        marginTop: '0.125rem',
                                                    }}
                                                    icon={
                                                        <span
                                                            style={{
                                                                alignItems: 'center',
                                                                display: 'inline-flex',
                                                                height: '100%',
                                                                justifyContent: 'center',
                                                                width: '100%',
                                                            }}
                                                        >
                                                            {option.icon}
                                                        </span>
                                                    }
                                                />
                                            </Col>
                                            <Col flex='auto'>
                                                <Typography.Text strong>{option.label}</Typography.Text>
                                                <Typography.Paragraph
                                                    style={{
                                                        color: token.colorTextTertiary,
                                                        margin: 0,
                                                    }}
                                                >
                                                    {option.description}
                                                </Typography.Paragraph>
                                            </Col>
                                            <Col flex='none' style={{ alignSelf: 'flex-start' }}>
                                                <Switch
                                                    checked={checked}
                                                    disabled={disabled}
                                                    size='small'
                                                    style={{ marginTop: '0.125rem' }}
                                                    onChange={(checked) => toggleMethod(option.method, checked)}
                                                />
                                            </Col>
                                        </Row>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default AuthorizedMethodsSection;
